import { TURN_SECONDS_MAX, TURN_SECONDS_MIN } from '@xox/shared'
import type { ReactNode } from 'react'

/** The turn-clock slider every duel's settings form shares. */
function TurnSecondsField({
  value,
  onChange,
  disabled,
}: {
  value: number
  onChange: (turnSeconds: number) => void
  disabled?: boolean
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor="turn-seconds" className="text-sm font-medium">
          Time per turn
        </label>
        <span className="text-sm tabular-nums font-semibold">{value} seconds</span>
      </div>
      <input
        id="turn-seconds"
        type="range"
        min={TURN_SECONDS_MIN}
        max={TURN_SECONDS_MAX}
        step={5}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-slate-900 dark:accent-white"
        aria-valuetext={`${value} seconds`}
      />
      <div className="flex justify-between text-[11px] text-slate-500">
        <span>{TURN_SECONDS_MIN}s · quick</span>
        <span>{TURN_SECONDS_MAX}s · relaxed</span>
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
        Run out of time on your move and you lose that game.
      </p>
    </div>
  )
}

export interface SettingsFormProps<S> {
  value: S
  onChange: (next: S) => void
  disabled?: boolean
}

/** The "Game settings" fieldset every duel form is: its own fields first, the turn clock last. */
export function SettingsFieldset<S extends { turnSeconds: number }>({
  value,
  onChange,
  disabled,
  children,
}: SettingsFormProps<S> & { children?: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-4" disabled={disabled}>
      <legend className="text-sm font-medium">Game settings</legend>
      {children}
      <TurnSecondsField
        value={value.turnSeconds}
        disabled={disabled}
        onChange={(turnSeconds) => onChange({ ...value, turnSeconds })}
      />
    </fieldset>
  )
}
